import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Snackbar,
  Switch,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { mergeIntegrationService } from "services/mergeIntegration";
import { MergeDataProps } from "./ATSIntegrationSettings";

interface ClientProvidedATSConfigProps {
  expertId: string;
  mergeData: MergeDataProps | null;
  activeStatus: boolean;
  onSettingsUpdated?: () => void;
  setActiveATS?: (ats: string) => void;
  activeATS?: string;
}

const ClientProvidedATSConfig: React.FC<ClientProvidedATSConfigProps> = ({
  expertId,
  mergeData,
  activeStatus,
  onSettingsUpdated,
  setActiveATS
}) => {
  const [status, setStatus] = useState<"not_connected" | "pending" | "connected">(
    "not_connected"
  );
  const [magicLink, setMagicLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncEnabled, setSyncEnabled] = useState(mergeData?.enableSync ?? false);
  const [jobSyncEnabled, setJobSyncEnabled] = useState(mergeData?.enableJobSync ?? false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<string>("");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    type: "success" | "error" | "info" | "warning" | "";
    title: string;
    message: string;
  }>({ open: false, type: "", title: "", message: "" });

  useEffect(() => {
    if (mergeData?.lastUpdated) {
      const formatted = new Intl.DateTimeFormat("en-GB", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(mergeData.lastUpdated));
      setLastSyncTime(formatted);
    }
  }, [mergeData]);

  useEffect(() => {
    if (activeStatus) setStatus("connected");
  }, [activeStatus]);

  /** 🔹 Snackbar Helper */
  const showSnackbar = useCallback(
    (type: "success" | "error" | "info" | "warning", title: string, message: string) => {
      setSnackbar({ open: true, type, title, message });
    },
    []
  );

  /** 🔹 Toggle Handler (for both sync types) */
  const handleToggle = useCallback(
    async (field: "sync" | "jobSync", newValue: boolean) => {
      if (!expertId) {
        showSnackbar("error", "Missing Expert ID", "Cannot update sync settings.");
        return;
      }

      setToggleLoading(true);

      // optimistic UI update
      if (field === "sync") setSyncEnabled(newValue);
      else setJobSyncEnabled(newValue);

      try {
        const response = await mergeIntegrationService.postSyncMergeSettings({
          expertId,
          enableSync: field === "sync" ? newValue : syncEnabled,
          enableJobSync: field === "jobSync" ? newValue : jobSyncEnabled,
        });

        console.log("Sync settings updated:", response);

        const title =
          field === "sync"
            ? "Data Sync Updated"
            : "Job Sync Updated";

        const message = newValue
          ? `${field === "sync" ? "Data" : "Job"} synchronization enabled successfully.`
          : `${field === "sync" ? "Data" : "Job"} synchronization disabled.`;

        showSnackbar("success", title, message);

        // set new sync time if enabled
        if (newValue) {
          const now = new Intl.DateTimeFormat("en-GB", {
            dateStyle: "medium",
            timeStyle: "short",
          }).format(new Date());
          setLastSyncTime(now);
        }
      } catch (err: any) {
        console.error("Error updating sync status:", err);
        const errorMsg =
          err?.response?.data?.message ||
          `Failed to update ${field === "sync" ? "data" : "job"} sync setting.`;

        showSnackbar("error", "Update Failed", errorMsg);

        // rollback optimistic UI update
        if (field === "sync") setSyncEnabled((prev) => !newValue);
        else setJobSyncEnabled((prev) => !newValue);
      } finally {
        setToggleLoading(false);
      }
    },
    [expertId, syncEnabled, jobSyncEnabled, showSnackbar]
  );

  /** 🔹 Magic Link Creation */
  const handleGenerateMagicLink = async () => {
    try {
      setLoading(true);
      const response = await mergeIntegrationService.postMagicLink({ expertId });
      const link = response?.output.magicLinks?.[0]?.magic_link_url ?? "";
      setMagicLink(link);
      setStatus("pending");
      showSnackbar(
        "info",
        "Magic Link Generated",
        "Share this link with your client to connect their ATS."
      );
    } catch {
      showSnackbar("error", "Failed", "Unable to generate magic link.");
    } finally {
      setLoading(false);
    }
  };

  /** 🔹 Delete Connection */
  const handleDeleteConnection = async () => {
    setLoading(true);
    await mergeIntegrationService.deleteMergeSetting({ expertId });
    setMagicLink("");
    setStatus("not_connected");
    setSyncEnabled(false);
    setJobSyncEnabled(false);
    setLoading(false);
    setOpenDeleteDialog(false);
    showSnackbar(
      "error",
      "Connection Deleted",
      "ATS connection removed and synchronization disabled."
    );
    setActiveATS?.("");
    onSettingsUpdated?.();
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(magicLink);
    showSnackbar("info", "Copied", "Magic link copied to clipboard.");
  };

  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mt: 2 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            {mergeData?.integration
              ? `Client-Provided ATS - ${mergeData.integration}`
              : 'Client-Provided ATS'}
          </Typography>
          {status !== "connected" &&
            <Typography color="text.secondary">
              Connect your client’s ATS using Merge integration
            </Typography>
          }
        </Box>
        {status === "connected" ? (
          <Chip
            label="Connected"
            color="success"
            icon={<CheckCircleIcon sx={{ color: "white !important" }} />}
          />
        ) : (
          <Chip label="Not Connected" variant="outlined" />
        )}
      </Box>

      {/* --- 1️⃣ No ATS Connected --- */}
      {status === "not_connected" && (
        <Box
          sx={{
            border: "2px dashed #E0E0E0",
            borderRadius: 2,
            p: 3,
            textAlign: "center",
          }}
        >
          <LinkIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
          <Typography fontWeight={600}>No ATS Connected</Typography>
          <Typography color="text.secondary" mb={2}>
            Generate a secure magic link to allow your client to connect their ATS system
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              loading ? <CircularProgress size={18} color="inherit" /> : <LinkIcon />
            }
            disabled={loading}
            onClick={handleGenerateMagicLink}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {loading ? "Generating..." : "Generate Magic Link"}
          </Button>
        </Box>
      )}

      {/* --- 2️⃣ Pending Connection --- */}
      {status === "pending" && (
        <>
          <Typography fontWeight={600} mb={1}>
            Magic Link
          </Typography>
          <TextField
            fullWidth
            value={magicLink}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleCopyLink}>
                    <ContentCopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <Typography color="text.secondary" mb={2}>
            Share this link with your client to connect their ATS
          </Typography>
          <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
            Refresh the page after your client completes the connection to see the updated status.
          </Alert>
        </>
      )}

      {/* --- 3️⃣ Connected --- */}
      {status === "connected" && (
        <>
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            p={2}
            border="1px solid"
            borderColor="divider"
            borderRadius={2}
            mb={2}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              {/* 🔹 Enable Data Sync (Left) */}
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip
                  title="Automatically synchronize candidate data between systems."
                  arrow
                  placement="top"
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ cursor: "help" }}
                  >
                    Enable Data Sync
                  </Typography>
                </Tooltip>

                {toggleLoading && syncEnabled && (
                  <CircularProgress size={16} color="primary" thickness={5} />
                )}

                <Switch
                  checked={syncEnabled}
                  onChange={(e) => handleToggle("sync", e.target.checked)}
                  color="primary"
                  disabled={toggleLoading}
                />
              </Box>

              {/* 🔹 Enable Job Sync (Right) */}
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip
                  title="Automatically synchronize job data between systems."
                  arrow
                  placement="top"
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ cursor: "help" }}
                  >
                    Enable Job Sync
                  </Typography>
                </Tooltip>

                {toggleLoading && jobSyncEnabled && (
                  <CircularProgress size={16} color="primary" thickness={5} />
                )}

                <Switch
                  checked={jobSyncEnabled}
                  onChange={(e) => handleToggle("jobSync", e.target.checked)}
                  color="primary"
                  disabled={toggleLoading}
                />
              </Box>
            </Box>
          </Box>

          {lastSyncTime && (
            <Typography color="text.secondary" mb={2}>
              Last synchronized: {lastSyncTime}
            </Typography>
          )}

          <Button
            variant="contained"
            color="error"
            fullWidth
            startIcon={<DeleteIcon />}
            disabled={loading}
            onClick={() => setOpenDeleteDialog(true)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Delete ATS Connection
          </Button>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={600}>Delete ATS Connection?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will disconnect the client’s ATS, stop all synchronization, and remove saved credentials.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDeleteDialog(false)} variant="outlined" color="inherit" sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConnection} variant="contained" color="error" sx={{ textTransform: "none" }}>
            Delete Connection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.type || "info"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          <Typography fontWeight={600}>{snackbar.title}</Typography>
          <Typography fontSize={14}>{snackbar.message}</Typography>
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ClientProvidedATSConfig;
